import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { post } from "../../helper/api_helper";
import "./user_detail.css";

/* ─── Constants ──────────────────────────────────────────── */
const CRAVING_META = {
  Strong:   { emoji: "😤", color: "#ef4444", bg: "#fef2f2" },
  Moderate: { emoji: "😐", color: "#f59e0b", bg: "#fffbeb" },
  Mild:     { emoji: "😊", color: "#10b981", bg: "#ecfdf5" },
  None:     { emoji: "🎉", color: "#6366f1", bg: "#eef2ff" },
};

/* ─── Icon ───────────────────────────────────────────────── */
const IconArrow = ({ dir }) => (
  <svg
    width="14" height="14"
    viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round"
  >
    {dir === "left"
      ? <polyline points="15 18 9 12 15 6" />
      : <polyline points="9 6 15 12 9 18" />}
  </svg>
);


/* ─── Main Component ─────────────────────────────────────── */
const Detail = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const mobile_no = location.state?.mobile_no;

  /* ── State ── */
  const [activeTab,    setActiveTab]    = useState("overview");
  const [expandedLog,  setExpandedLog]  = useState(null);
  const [loading,      setLoading]      = useState(true);

  // API data
  const [userInfo,     setUserInfo]     = useState(null);   // overview + products
  const [depQuestions, setDepQuestions] = useState([]);     // dependence test
  const [dailyLogs,    setDailyLogs]    = useState([]);     // daily log list

  /* ── Redirect if no mobile_no ── */
  useEffect(() => {
    if (!mobile_no) navigate("/tobacco_users");
  }, [mobile_no, navigate]);

  /* ── Alert helper (same pattern as Tobacco_user.jsx) ── */
  const showAlert = (icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  };

  /* ── Fetch: User Overview ── */
  const fetchUserInfo = async () => {
    try {
      const response = await post("tobacco_user_view", { mobile_no });
      if (response.status === 200) { 
        setUserInfo(response.data[0]);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  /* ── Fetch: Dependence Questions ── */
  const fetchDependenceQuestions = async () => {
    try {
      const response = await post("tobacco_user_dependence", { mobile_no });
      if (response.status === 200) {
        setDepQuestions(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  /* ── Fetch: Daily Logs ── */
  const fetchDailyLogs = async () => {
    try {
      const response = await post("tobacco_user_daily_log", { mobile_no });
      if (response.status === 200) {
        setDailyLogs(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  /* ── Fetch all on mount ── */
  useEffect(() => {
    if (!mobile_no) return;
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchUserInfo(),
        fetchDependenceQuestions(),
        fetchDailyLogs(),
      ]);
      setLoading(false);
    };
    loadAll();
  }, [mobile_no]);

  /* ── Derived stats for Daily Log summary ── */
    const smokeTotal = dailyLogs.reduce((sum, log) => {
    return (
        sum +
        (log.daily_log_detail_lists || []).reduce((s, item) => {
        return item.tobacco_product_type === 1
            ? s + Number(item.tobacco_usage || 0)
            : s;
        }, 0)
    );
    }, 0);

    const smokelessTotal = dailyLogs.reduce((sum, log) => {
    return (
        sum +
        (log.daily_log_detail_lists || []).reduce((s, item) => {
        return item.tobacco_product_type === 2
            ? s + Number(item.tobacco_usage || 0)
            : s;
        }, 0)
    );
    }, 0);

    const totalSpend = dailyLogs.reduce(
    (s, l) => s + Number(l.total_spend || 0),
    0
    );

    const avgSpend = dailyLogs.length
    ? Math.round(totalSpend / dailyLogs.length)
    : 0;

  /* ── Dependence answered count ── */
  const answeredCount = depQuestions.filter(q => q.userAnswer).length;

  /* ── Loading state ── */
  if (loading) {
    return (
      <div className="tud">
        <div className="tud-loading">
          <div className="tud-spinner" />
          Loading user details…
        </div>
      </div>
    );
  }

  /* ── Guard: no user data ── */
  if (!userInfo) return null;

  /* ── Shorthand aliases (map API field names) ── */
  const user = {
    username:   userInfo.username,
    mobile:     userInfo.mobile_no,
    age:        userInfo.age,
    location:   userInfo.location,
    products:   userInfo.tobacco_user_product || [], 
    yearOfUse:  userInfo.year_of_use,
    perDaySpend:userInfo.per_day_spend,
    score:      userInfo.score,
  };
  const initials = user.username?.slice(0, 2).toUpperCase() || "U";
  const totals = (user.products || []).reduce(
    (acc, item) => {
      if (item.tobacco_product_type == 1) acc.smoke += Number(item.use_qty) || 0;
      else if (item.tobacco_product_type == 2) acc.smokeless += Number(item.use_qty) || 0;
      return acc;
    },
    { smoke: 0, smokeless: 0 }
  );
  /* ─────────────────────────────────────────────────────── */
  return (
    <React.Fragment>
      <div className="tud">

        {/* ── Topbar ── */}
        <div className="tud-top">
          <div className="tud-hero">
            <div className="tud-avatar">{initials}</div>
            <div>
              <h1 className="tud-name">{user.username}</h1>
              <p className="tud-sub">{user.mobile} · {user.location}</p>
            </div>
            <div className="tud-score">
              <div className="v">{user.score}</div>
              <div className="l">Score</div>
            </div>
          </div>
        </div>

        {/* ── Stats Strip ── */}
        <div className="tud-stats">
          <div className="stat-card">
            <div className="sl">Age</div>
            <div className="sv">{user.age}</div>
            <div className="ss">years old</div>
          </div>
          <div className="stat-card">
            <div className="sl">Experience</div>
            <div className="sv">{user.yearOfUse}</div>
            <div className="ss">years of use</div>
          </div>
          <div className="stat-card">
            <div className="sl">Per Day</div>
            <div className="sv">₹{user.perDaySpend}</div>
            <div className="ss">spend</div>
          </div>
          <div className="stat-card">
            <div className="sl">Products</div>
            <div className="prod-chips">
                <span className="prod-chip" key="smake">
                  SMOKE ({totals.smoke})
                </span>
                <span className="prod-chip" key="smakeless">
                  SMOKELESS ({totals.smokeless})
                </span>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="tud-tabs">
          {["overview", "dependence", "daily log"].map(t => (
            <button
              key={t}
              className={`tud-tab${activeTab === t ? " active" : ""}`}
              onClick={() => setActiveTab(t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* ── Tab Content ── */}
        <div className="tud-body">

        {/* ── OVERVIEW ── */}
        {activeTab === "overview" && (
            <div className="ov-layout">
 
              {/* LEFT — Personal Details */}
              <div className="info-card ov-left">
                <div className="info-card-hd">
                  <span className="dot" /> Personal Details
                </div>
                <div className="ov-details-list">
                  {[
                    { icon: "👤", label: "Username",      val: user.username,              cls: ""      },
                    { icon: "📱", label: "Mobile No.",    val: user.mobile,                cls: "mono"  },
                    { icon: "🎂", label: "Age",           val: `${user.age} years`,        cls: ""      },
                    { icon: "📍", label: "Location",      val: user.location,              cls: ""      },
                    { icon: "📅", label: "Years of Use",  val: `${user.yearOfUse} yrs`,    cls: ""      },
                    { icon: "💰", label: "Per Day Spend", val: `₹${user.perDaySpend}`,     cls: "green" },
                  ].map(({ icon, label, val, cls }) => (
                    <div className="ov-detail-row" key={label}>
                      <span className="ov-detail-icon">{icon}</span>
                      <div className="ov-detail-body">
                        <span className="ov-detail-label">{label}</span>
                        <span className={`ov-detail-val${cls ? ` ${cls}` : ""}`}>{val}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
 
              {/* RIGHT — Products + Score */}
              <div className="ov-right">
 
                {/* Products */}
                <div className="info-card ov-products">
                  <div className="info-card-hd">
                    <span className="dot" style={{ background: "var(--accent)" }} />
                    Tobacco Products
                  </div>
                  <div className="ov-prod-list">
                    {user.products.length === 0 && (
                      <p className="ov-empty">No products recorded.</p>
                    )}
                    {user.products.map(p => (
                      <div className="ov-prod-row" key={p.tobacco_product_name}>
                        <span className="ov-prod-icon">{p.tobacco_product_emoji}</span>
                        <span className="ov-prod-name">{p.tobacco_product_name}</span>
                        <span className="ov-prod-qty">{p.use_qty}<em>/day</em></span>
                      </div>
                    ))}
                  </div>
                </div>
 
                {/* Score */}
                <div className="info-card ov-score-card">
                  <div className="info-card-hd">
                    <span className="dot" style={{ background: "var(--accent2)" }} />
                    Dependence Score
                  </div>
                  <div className="ov-score-body">
                    <div className="ov-score-dial">
                      <span className="ov-score-num">{user.score}</span>
                      <span className="ov-score-denom">/10</span>
                    </div>
                    <div className="ov-score-right">
                      <div className="score-bar-track">
                        <div className="score-bar-fill" style={{ width: `${user.score * 10}%` }} />
                      </div>
                      <div className="score-bar-labels">
                        {["Low", "Moderate", "High", "Very High"].map(l => (
                          <span key={l}>{l}</span>
                        ))}
                      </div>
                      <p className="ov-score-label">Dependence Score</p>
                    </div>
                  </div>
                </div>
 
              </div>
            </div>
          )}
 

          {/* ── DEPENDENCE ── */}
          {activeTab === "dependence" && (
            <>
              <div className="dep-hd">
                <h2><em>Dependence</em> <span>Test</span></h2>
                <p>6 clinically validated questions · Takes ~60 seconds</p>
                <div className="dep-prog">
                  <div className="dep-prog-bar">
                    <div
                      className="dep-prog-fill"
                      style={{
                        width: depQuestions.length
                          ? `${(answeredCount / depQuestions.length) * 100}%`
                          : "0%"
                      }}
                    />
                  </div>
                  <span className="dep-prog-lbl">
                    {answeredCount} / {depQuestions.length} answered
                  </span>
                </div>
              </div>

              {depQuestions.length === 0 && (
                <p style={{ color: "var(--text3)", textAlign: "center", padding: "32px 0", fontSize: 14 }}>
                  No dependence test data available.
                </p>
              )}

              {depQuestions.map((q, idx) => (
                <div className="dep-q" key={q.question_id || idx}>
                  <div className="dep-q-top">
                    <div className="dep-q-id">Q{idx + 1}</div>
                    <div className="dep-q-text">{q.question_text}</div>
                  </div>
                  {q.question_options && q.question_options.length > 0 ? (
                    <div className="dep-opts">
                        {(q.question_options || []).map(opt => (
                        <div
                            key={opt.question_option_id}
                            className={`dep-opt${q.userAnswer === opt.question_option_id ? " sel" : ""}`}
                        >
                            {opt.option_label}
                        </div>
                        ))}
                    </div>
                  ) : (
                    <p className="no-options">No options available</p>
                  )}
                </div>
              ))}
            </>
          )}

          {/* ── DAILY LOG LIST ── */}
          {activeTab === "daily log" && (
            <>
              {/* Summary strip */}
              <div className="dl-sum">
                {[
                  { sl: "Total Logs",       sv: dailyLogs.length, ss: "days recorded",   color: "var(--primary)" },
                  { sl: "Total Smoke", sv: smokeTotal,         ss: "Total consumed", color: "var(--accent)"  },
                  { sl: "Total Smokeless",     sv: smokelessTotal,       ss: "Total consumed", color: "#8b5cf6"        },
                  { sl: "Avg Daily Spend",  sv: `₹${avgSpend}`,   ss: "per day average", color: "var(--accent2)" },
                ].map(({ sl, sv, ss, color }) => (
                  <div className="dl-sum-card" key={sl}>
                    <div className="sl">{sl}</div>
                    <div className="sv" style={{ color }}>{sv}</div>
                    <div className="ss">{ss}</div>
                  </div>
                ))}
              </div>

              <div className="dl-list-hd">
                <h3>Log History</h3>
                <span>{dailyLogs.length} entries</span>
              </div>

              {dailyLogs.length === 0 && (
                <p style={{ color: "var(--text3)", textAlign: "center", padding: "32px 0", fontSize: 14 }}>
                  No daily log data available.
                </p>
              )}

              {dailyLogs.map((log, idx) => {
                const isOpen = expandedLog === (log.user_daily_log_id || idx);
                const meta   = CRAVING_META[log.craving] || CRAVING_META.Mild;
                const spend  = log.total_spend || 0;
                const target = log.target      || 0;
                const pct    = target > 0 ? Math.min(100, Math.round((spend / target) * 100)) : 0;
                const under  = spend <= target;

                // Parse date string e.g. "Fri, 13 Mar 2025"
                const afterComma = (log.date_added || "").split(", ")[1] || "";
                const parts   = afterComma.split(" ");
                const dayNum  = parts[0] || "--";
                const month   = parts[1] || "";
                const yearStr = parts[2] || "";
                const dayName = (log.date_added || "").split(",")[0] || "";

                return (
                  <div
                    key={log.user_daily_log_id || idx}
                    className={`dl-card${isOpen ? " open" : ""}`}
                    onClick={() => setExpandedLog(isOpen ? null : (log.user_daily_log_id || idx))}
                  >
                    <div className="dl-card-row">
                      {/* Date Badge */}
                      <div className="dl-date">
                        <span className="dd">{dayNum}</span>
                        <span className="dm">{month}</span>
                      </div>

                      {/* Middle */}
                      <div className="dl-mid">
                        <div className="dl-mid-title">
                          {dayName} · {month} {dayNum}, {yearStr}
                        </div>
                        <div className="dl-pills">
                            {(log.daily_log_detail_lists || []).map((item) => (
                            <span key={item.daily_log_detail_id} className="dl-pill">
                                {item.tobacco_product_emoji} <b>{item.tobacco_usage}/{item.use_qty}</b> {item.tobacco_product_name.trim()}
                            </span>
                            ))}
                          {log.notes && (
                            <span className="dl-pill">📝 Note</span>
                          )}
                        </div>
                      </div>

                      {/* Right */}
                      <div className="dl-right">
                        <span className="dl-spend">₹{spend}</span>
                      </div>

                      <div className={`dl-chev${isOpen ? " open" : ""}`}>
                        <IconArrow dir="right" />
                      </div>
                    </div>

                    {/* Expanded Detail */}
                    {isOpen && (
                      <div
                        className="dl-detail"
                        onClick={e => e.stopPropagation()}
                      >

                        {log.notes && (
                          <div className="dl-dblock full">
                            <div className="dlbl">Note</div>
                            <div className="note-box">"{log.notes}"</div>
                          </div>
                        )}

                        {target > 0 && (
                          <div className="dl-dblock full">
                            <div className="dl-bar-wrap">
                              <div className="bar-row">
                                <span className="bar-lbl">
                                  Spend vs Daily Target (₹{target})
                                </span>
                                <span className={`bar-val ${under ? "under" : "over"}`}>
                                  {under
                                    ? `▼ ₹${target - spend} under`
                                    : `▲ ₹${spend - target} over`}
                                </span>
                              </div>
                              <div className="dl-bar-track">
                                <div
                                  className="dl-bar-fill"
                                  style={{
                                    width: `${pct}%`,
                                    background: under
                                      ? "linear-gradient(90deg,#10b981,#34d399)"
                                      : "linear-gradient(90deg,#ef4444,#f87171)",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Detail;